import { redirect } from '@remix-run/node';
import NoImageAvailable from '~/images/no_image_available.svg';
import type {
  AttributeValues,
  ItemAttributes,
  PackItem,
  PackProduct,
  Product,
  ProductsPrices,
} from '~/services/api.server';
import { isCustomPackOrProduct, isPackProduct } from '~/utils/common';
import { AttributesKeys } from '~/utils/constants';

export type MediaListProduct = {
  id: number;
  image?: string;
  name: string;
  slug?: string;
  type: number;
  products?: Array<Product>;
  quantity?: number;
  color?: string;
};

type ColorAttributes = {
  product_id: number;
  attributes: { name: string; value: string }[];
};

export type MediaListProductQueue = MediaListProduct & {
  quantity?: number;
  items_attributes?: ColorAttributes[];
};

export enum MediaContentType {
  IMAGE = 1,
  VIDEO = 2,
  PDF = 3,
  OTHER = 4,
}

export function fillMediaList(
  product: Product,
  page: number,
  isDesignLab: boolean = false,
  items_attributes: ItemAttributes[],
  quantity?: number
) {
  const productList: MediaListProduct[] = [];
  const query = getColorId(items_attributes);

  if (product) {
    if (isPackProduct(product)) {
      if (page === 1) {
        if (!isDesignLab) {
          productList.push({
            id: product.id,
            image: product.image,
            name: product.name,
            slug: product.slug,
            type: MediaContentType.IMAGE,
          });
        }

        productList.push({
          id: product.container.id,
          image: isDesignLab && (product.container.blank_image || product.container.image),
          name: product.container.name,
          slug: product.container.slug,
          type: MediaContentType.IMAGE,
          color: query,
        });

        if (product.filler) {
          productList.push({
            id: product.filler.id,
            image: isDesignLab && (product.filler.blank_image || product.filler.image),
            name: product.filler.name,
            slug: product.filler.slug,
            type: MediaContentType.IMAGE,
            color: query,
          });
        }

        product.items?.forEach((item: PackItem, index) => {
          if (product.filler && index >= (isDesignLab ? 4 : 3)) {
            return;
          }

          if (index >= (isDesignLab ? 5 : 4)) {
            return;
          }

          const prod = (item.supplier_product || item.product || item) as Product;

          productList.push({
            id: prod.id,
            image: isDesignLab && (prod.blank_image || prod.image),
            name: prod.name,
            slug: prod.slug,
            type: MediaContentType.IMAGE,
            color: query,
          });
        });
      } else {
        product.items?.forEach((item: PackItem, index) => {
          if (product.filler && index < (page - 1) * (isDesignLab ? 7 : 6) - 3) {
            return;
          }

          if (!product.filler && index < (page - 1) * (isDesignLab ? 7 : 6) - 2) {
            return;
          }
          const prod = (item.supplier_product || item.product || item) as Product;

          productList.push({
            id: prod.id,
            image: isDesignLab && (prod.blank_image || prod.image),
            name: prod.name,
            slug: prod.slug,
            type: MediaContentType.IMAGE,
            color: query,
          });
        });
      }
    } else {
      productList.push({
        id: product.id,
        image: isDesignLab && (product.blank_image || product.image || NoImageAvailable),
        name: product.name,
        slug: product.slug,
        quantity,
        type: MediaContentType.IMAGE,
        color: query,
      });

      // if (product && product.media_contents && product.media_contents.length > 0) {
      //   if (page === 1) {
      //     productList.push({
      //       id: product.id,
      //       image: product.image,
      //       name: product.name,
      //       type: MediaContentType.IMAGE,
      //     });
      //     for (const [index, mediaContent] of product.media_contents!.entries()) {
      //       if (index >= 5) {
      //         break;
      //       }
      //       productList.push({
      //         id: mediaContent.id,
      //         image: mediaContent.media,
      //         name: mediaContent.media.substring(mediaContent.media.lastIndexOf('/') + 1),
      //         type: mediaContent.type.id,
      //       });
      //     }
      //   } else {
      //     for (const [index, mediaContent] of product.media_contents!.entries()) {
      //       if (index < (page - 1) * 6 - 1) {
      //         continue;
      //       }
      //       productList.push({
      //         id: mediaContent.id,
      //         image: mediaContent.media,
      //         name: mediaContent.media.substring(mediaContent.media.lastIndexOf('/') + 1),
      //         type: mediaContent.type.id,
      //       });
      //     }
      //   }
      // }
    }
  }
  return productList;
}

export function defaultAttributes(product: Product): ItemAttributes[] {
  if (isPackProduct(product)) {
    const container = {
      product_id: product.container.id,
      attributes: Object.keys(product.container.available_attributes).map((name) => {
        const key: string = AttributesKeys[name as keyof typeof AttributesKeys];
        return { name: key, value: product.container.available_attributes[name][0].name };
      }),
    };

    let filler;

    if (product.filler) {
      filler = {
        product_id: product.filler.id,
        attributes: Object.keys(product.filler.available_attributes).map((name) => {
          const key: string = AttributesKeys[name as keyof typeof AttributesKeys];
          return { name: key, value: product.filler.available_attributes[name][0].name };
        }),
      };
    }
    const items = product.items?.map((item: PackItem) => {
      const itemProduct: Product | undefined = item.supplier_product || item.product;
      if (!itemProduct) return undefined;
      return {
        product_id: itemProduct.id,
        attributes: Object.keys(itemProduct?.available_attributes || {}).map((name) => {
          const key: string = AttributesKeys[name as keyof typeof AttributesKeys];
          return { name: key, value: itemProduct?.available_attributes[name][0].name };
        }),
      };
    });

    let packAttributes;

    if (filler) {
      packAttributes = items.concat(container).concat(filler);
    } else {
      packAttributes = items.concat(container);
    }

    return packAttributes as ItemAttributes[];
  } else return [];
}

export function defaultProductAttributes(product: Product): AttributeValues[] {
  if (!isPackProduct(product)) {
    const result: AttributeValues[] = [
      {
        attributes: Object.keys(product.available_attributes).map((name) => {
          const key: string = AttributesKeys[name as keyof typeof AttributesKeys];
          return { name: key, value: product?.available_attributes[name][0].name };
        }),
      },
    ];
    return result;
  } else return [];
}

export function getPackPrice(productId: number, prices: ProductsPrices) {
  const price = prices.products.find((price) => {
    return price.product === productId;
  });
  return price?.price?.amount;
}

export type UnavailableVariant = {
  id: number;
  variants: [
    {
      [key: string]: string;
    }
  ];
};

function fillUnavailableVariants(unavailableVariants: UnavailableVariant[], product?: Product) {
  if (!product) return;
  let emptyVariant: any;
  const tempVariants: [{ [key: string]: string }] = [{}];
  product.unavailable_variants.forEach((variant) => {
    emptyVariant = Object.assign({}, product.available_attributes);

    for (const key in emptyVariant) {
      emptyVariant[key] = [];
    }

    const variants = variant.split(',');

    Object.keys(emptyVariant).forEach((key, index) => {
      emptyVariant[key] = variants[index];
    });

    tempVariants.push(emptyVariant);
  });

  unavailableVariants.push({
    id: product.id,
    variants: tempVariants,
  });
}

export function getPackUnavailableVariants(product: PackProduct) {
  const unavailableVariants: UnavailableVariant[] = [];

  fillUnavailableVariants(unavailableVariants, product.container);
  fillUnavailableVariants(unavailableVariants, product.filler);

  for (let item of product.items) {
    fillUnavailableVariants(unavailableVariants, item.product);
  }

  return unavailableVariants;
}

export function getProductUnavailableVariants(product: Product) {
  const unavailableVariants: UnavailableVariant[] = [];

  fillUnavailableVariants(unavailableVariants, product);

  return unavailableVariants;
}

// redirects user to a designable item or back to shop
export function handleDesignLabReadyItems(product: Product | PackProduct, id: string | ItemAttributes[]) {
  if (isPackProduct(product)) {
    const items = [product.container, product.filler, ...product.items].filter((item) => item != null);
    const itemCanBeDesigned = items.find((item) => {
      if (item?.design_lab_ready) {
        return item.design_lab_ready && !isCustomPackOrProduct(item);
      }
      return item?.product?.design_lab_ready && !isCustomPackOrProduct(item?.product);
    });

    if (!itemCanBeDesigned) {
      throw redirect('/shop');
    } else if (itemCanBeDesigned?.product?.slug) {
      throw redirect(`/design-lab/${product.slug}/items/${itemCanBeDesigned.product.slug}?id=${id}`);
    }
    throw redirect(`/design-lab/bulk/${itemCanBeDesigned.product?.slug}`);
  } else {
    if (product?.design_lab_ready && !isCustomPackOrProduct(product)) {
      throw redirect(`/design-lab/bulk/${product.slug}`);
    }
    throw redirect('/shop');
  }
}

export function getColorId(attributes: ItemAttributes[] | undefined) {
  if (!attributes) return '';
  return `${attributes
    .map((item) => {
      return `${item.product_id}-${item.attributes
        ?.map((attr) => {
          if (attr.name !== 'color') return '';
          return `${attr.name}:${attr.value}`;
        })
        .sort()
        .join('')}`;
    })
    .sort()
    .join('/')
    .replaceAll(' ', '')}`;
}

export function transformColor(urlColor: string | undefined) {
  if (!urlColor) {
    return undefined;
  }
  // Case 1: "DarkBlue" into "Dark Blue"
  const firstParse = urlColor.replace(/([a-z])([A-Z])/g, '$1 $2');

  // Case 2: "Orange(OR)" into "Orange (OR)"
  const secondParse = firstParse.replace(/\(/g, ' (').replace(/\)/g, ') ').trim();

  return secondParse;
}

export function isColorAvailable(color: string | undefined, product: Product) {
  if (!color) {
    return false;
  }

  const colorIsAvailable = product.available_attributes.colors.find((availableColor) => availableColor.name === color);
  return Boolean(colorIsAvailable);
}

export function generatePackAttributes(props: string): ItemAttributes[] {
  //Validate color
  try {
    const rawAttributes = props.split('/');
    const attributes = rawAttributes.map((prop) => {
      const attribute = prop.split('-color:');

      if (!attribute[1]) throw new Error('Color cant be undefined');

      return {
        product_id: attribute[0],
        attributes: [{ name: 'color', value: transformColor(attribute[1]) }],
      };
    });
    return attributes;
  } catch (error) {
    return [];
  }
}

export function getColorFromAttributes(attributes: ItemAttributes[], productId: number, defaultColor: number) {
  const id = attributes?.find((attribute) => Number(attribute.product_id) === Number(productId));

  return id ? id.attributes?.[0].value : defaultColor;
}
